import { createHubspotForm } from '../hubspot';

const RECAPTCHA_SITE_KEY = '6LdrYG0sAAAAACseFKM7g25ZUOPjlieu7azQ6upq';

let recaptchaPromise = null;

const translations = {
    sv: {
        requiredField: 'Obligatoriskt fält',
        invalidEmail: 'Ej giltig e-mail',
    },
    en: {
        requiredField: 'Required field',
        invalidEmail: 'Invalid email address',
    },
};

/**
 * Returns the translation strings for the current page language.
 * Falls back to Swedish if the language is not found.
 *
 * @returns {object} Translation strings
 */
const getTranslations = () => {
    console.log('Current page language:', document.documentElement.lang);
    const lang = document.documentElement.lang?.slice(0, 2) || 'sv';
    return translations[lang] || translations.sv;
};

/**
 * Creates an error message element and inserts it before the given field.
 *
 * @param {HTMLElement} field - The form field to attach the message to
 * @param {string} message - The error text to display
 */
const addFieldError = (field, message) => {
    // Avoid duplicates – one error per field at a time
    const existing = field.parentElement.querySelector(
        `.js-field-error[data-for="${field.getAttribute('name')}"]`
    );
    if (existing) return;

    const el = document.createElement('span');
    el.className = 'js-field-error field-error-fade';
    el.setAttribute('data-for', field.getAttribute('name'));
    el.textContent = message;
    field.parentElement.appendChild(el);
};

/**
 * Removes all existing field-level error messages inside a form container.
 *
 * @param {HTMLElement} form - The form container element
 */
const clearFieldErrors = (form) => {
    form.querySelectorAll('.js-field-error').forEach((el) => el.remove());
};

const pushDataLayer = (event) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event });
};

/**
 * Loads Google reCAPTCHA v3 for our own (non-HubSpot) forms.
 * Only loaded when such a form exists – a global grecaptcha collides with
 * HubSpot's reCAPTCHA Enterprise and makes HubSpot submissions fail.
 *
 * @returns {Promise<void>}
 */
const loadRecaptcha = () => {
    if (recaptchaPromise) return recaptchaPromise;

    recaptchaPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.setAttribute('data-cookieconsent', 'ignore');
        script.addEventListener('load', resolve);
        script.addEventListener('error', () => {
            recaptchaPromise = null;
            reject(new Error('reCAPTCHA not loaded'));
        });
        document.head.appendChild(script);
    });

    return recaptchaPromise;
};

const getRecaptchaToken = async (action) => {
    await loadRecaptcha();

    return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
            grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
                .then(resolve)
                .catch(reject);
        });
    });
};

const initFooterForm = () => {
    // Initialize modal toggle functionality
    initModalToggle();

    const forms = document.querySelectorAll('.js-footer-form');

    forms.forEach(form => {
        const button = form.querySelector('.js-post-footer-demo');
        const inputs = form.querySelectorAll('input');

        if (!button || !inputs.length) return;

        loadRecaptcha().catch((error) => console.error(error));

        // Determine form type based on form classes
        const formType = form.classList.contains('js-footer-form-modal') ? 'top_menu' :
                        form.classList.contains('js-footer-form-footer') ? 'footer' : 'landingpage';

        let formStarted = false;
        let formInteracted = false;

        // Track form start on first input focus
        const inputFields = form.querySelectorAll('input[type="text"], input[type="email"], textarea');
        inputFields.forEach(input => {
            input.addEventListener('focus', () => {
                if (!formStarted) {
                    formStarted = true;
                    pushDataLayer(`book_demo_${formType}_form_start`);
                }
                formInteracted = true;

                // Clear validation error for this field on focus
                input.classList.remove('border-red-500', '!border-red-500');
                const fieldName = input.getAttribute('name');
                const error = input.parentElement?.querySelector(`.js-field-error[data-for="${fieldName}"]`);
                if (error) error.remove();
            });
        });

        // Track form dropoff when user leaves without submitting
        const trackDropoff = () => {
            if (formInteracted && formStarted) {
                pushDataLayer(`book_demo_${formType}_form_dropoff`);
                formInteracted = false;
            }
        };

        // Track dropoff on window blur or beforeunload
        window.addEventListener('blur', trackDropoff);
        window.addEventListener('beforeunload', trackDropoff);

        button.addEventListener('click', async (e) => {
            e.preventDefault();

            // Get all input values
            const formData = {};
            const inputFields = form.querySelectorAll('input[type="text"], input[type="email"]');
            const textareaField = form.querySelector('textarea');
            const policyCheckbox = form.querySelector('input[type="checkbox"]');
            const hiddenUrlField = form.querySelector('input[name="current_url"]');

            // Collect field values using name attributes
            inputFields.forEach(input => {
                const fieldName = input.getAttribute('name');
                if (fieldName) {
                    formData[fieldName] = input.value.trim();
                }
            });

            // Add current URL from hidden field
            if (hiddenUrlField) {
                formData.current_url = hiddenUrlField.value;
            }

            // Add message field (required)
            if (textareaField) {
                formData.message = textareaField.value.trim();
            }

            formData.policy = policyCheckbox ? policyCheckbox.checked : false;

            // Remove any existing error borders and messages
            clearFieldErrors(form);
            inputFields.forEach(input => {
                input.classList.remove('border-red-500', '!border-red-500');
            });
            if (textareaField) {
                textareaField.classList.remove('border-red-500', '!border-red-500');
            }
            if (policyCheckbox) {
                policyCheckbox.parentElement.classList.remove('text-red-500');
            }

            // Validate all required fields and mark invalid ones
            const invalidFields = validateAndMarkFields(formData, inputFields, policyCheckbox, textareaField);
            if (invalidFields.length > 0) {
                return;
            }

            // Get form inner element
            const formInner = form.querySelector('.js-form-inner');

            // Blur form during submission
            if (formInner) {
                formInner.classList.add('opacity-70', 'pointer-events-none');
            }

            try {
                // Get reCAPTCHA token
                const recaptchaToken = await getRecaptchaToken('demo_request');
                formData.recaptcha_token = recaptchaToken;

                const response = await fetch('/wp-json/curoflow/v1/demo-request', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData)
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    // Track successful submission
                    pushDataLayer(`book_demo_${formType}_submit`);
                    formInteracted = false; // Reset to prevent dropoff tracking

                    // Hide the form
                    const thankYou = form.querySelector('.js-thank-you');

                    if (formInner) {
                        formInner.classList.add('hidden');
                        formInner.classList.remove('opacity-70', 'pointer-events-none');
                    }
                    if (thankYou) {
                        thankYou.classList.remove('hidden');
                    }
                } else {
                    // Remove blur and show error
                    if (formInner) {
                        formInner.classList.remove('opacity-70', 'pointer-events-none');
                    }
                    form.classList.add('border-red-500');
                    setTimeout(() => {
                        form.classList.remove('border-red-500');
                    }, 3000);
                }
            } catch (error) {
                console.error('Error submitting form:', error);
                // Remove blur and show error
                if (formInner) {
                    formInner.classList.remove('opacity-70', 'pointer-events-none');
                }
                form.classList.add('border-red-500');
                setTimeout(() => {
                    form.classList.remove('border-red-500');
                }, 3000);
            }
        });

        // Allow form submission with Enter key
        inputs.forEach(input => {
            input.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    button.click();
                }
            });
        });
    });
};

const validateAndMarkFields = (data, inputFields, policyCheckbox, textareaField) => {
    const invalidFields = [];
    const t = getTranslations();

    // Validate and mark each field using name attributes
    inputFields.forEach(input => {
        const fieldName = input.getAttribute('name');
        let isValid = true;
        let errorMessage = t.requiredField;

        if (fieldName === 'name' && !data.name) {
            isValid = false;
        } else if (fieldName === 'country' && !data.country) {
            isValid = false;
        } else if (fieldName === 'job_title' && !data.job_title) {
            isValid = false;
        } else if (fieldName === 'organization' && !data.organization) {
            isValid = false;
        } else if (fieldName === 'email') {
            if (!data.email) {
                isValid = false;
            } else if (!isValidEmail(data.email)) {
                isValid = false;
                errorMessage = t.invalidEmail;
            }
        } else if (fieldName === 'phone' && !data.phone) {
            isValid = false;
        }

        if (!isValid) {
            input.classList.add('!border-red-500');
            addFieldError(input, errorMessage);
            invalidFields.push(input);
        }
    });

    // Validate message field (required)
    if (textareaField && !data.message) {
        textareaField.classList.add('!border-red-500');
        addFieldError(textareaField, t.requiredField);
        invalidFields.push(textareaField);
    }

    // Validate policy checkbox
    if (!data.policy && policyCheckbox) {
        policyCheckbox.parentElement.classList.add('text-red-500');
        invalidFields.push(policyCheckbox);
    }

    return invalidFields;
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const initModalToggle = () => {
    const modal = document.querySelector('.js-modal-form');
    const toggleButtons = document.querySelectorAll('.js-toggle-form-modal');
    const closeButtons = document.querySelectorAll('.js-toggle-modal');
    const hubspotContainer = modal?.querySelector('.js-popup-hubspot-form');
    let hubspotFormCreated = false;

    if (!modal) return;

    // Hide modal by default
    modal.classList.add('hidden');

    // Open modal
    toggleButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            modal.classList.remove('hidden');
            document.body.classList.add('overflow-hidden');

            // Create the HubSpot form first when the modal is visible,
            // HubSpot's invisible reCAPTCHA breaks if rendered while hidden
            if (hubspotContainer && !hubspotFormCreated) {
                hubspotFormCreated = true;
                createHubspotForm(hubspotContainer.dataset.hubspotId, '.js-popup-hubspot-form', {
                    // The confirmation is much shorter than the form, scroll up so it's in view on mobile
                    onSubmitted: () => modal.scrollTo({ top: 0 }),
                })
                    .catch((error) => {
                        hubspotFormCreated = false;
                        console.error(error);
                    });
            }
        });
    });

    // Close modal
    closeButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            modal.classList.add('hidden');
            document.body.classList.remove('overflow-hidden');
        });
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
            modal.classList.add('hidden');
            document.body.classList.remove('overflow-hidden');
        }
    });
};

const initDownloadTracking = () => {
    const downloadLinks = document.querySelectorAll('.js-track-download');

    downloadLinks.forEach(link => {
        link.addEventListener('click', () => {
            const fileName = link.getAttribute('data-file-name') || 'unknown';
            const fileType = link.getAttribute('data-file-type') || 'unknown';

            pushDataLayer('document_download');

            // Optional: Add additional data to the event
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'document_download',
                file_name: fileName,
                file_type: fileType
            });
        });
    });
};

export default initFooterForm;
export { initDownloadTracking };